import React from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import {
  FormGroup,
  Label,
  Input,
  Form,
  Col,
  Row,
  FormFeedback,
} from 'reactstrap';
import ArrayInput from './ArrayInput';
import NFButton from '../primitives/NFButton';

export default class FormBuilder extends React.Component {
  static getDerivedStateFromProps(nextProps, prevState) {
    return nextProps.fields.reduce((accum, field) => {
      // If the field was not just added, merge the potentially new schema with the old one,
      // overwriting the old schemas values
      if (prevState[field.name]) {
        return Object.assign(accum, {
          [field.name]: {
            ...prevState[field.name],
            ...nextProps.fields.find(newField => newField.name === field.name),
          },
        });
      }

      return {
        ...accum,
        [field.name]: FormBuilder.getInitialStateOfField(field),
      };
    }, {});
  }

  static getInitialStateOfField(fieldState) {
    // If the field was just added, initialize it's state
    let value;
    if (fieldState.type === 'text' || fieldState.type === 'custom') {
      value = '';
    } else if (fieldState.type === 'array') {
      value = [''];
    } else if (fieldState.type === 'select') {
      [value] = fieldState.options;
    } else if (fieldState.type === 'checkbox') {
      value = false;
    }

    return {
      ...fieldState,
      dirty: false,
      blurred: false,
      value,
    };
  }

  constructor(props) {
    super(props);

    this.state = {};
  }

  getValidationState(fieldName) {
    const fieldState = this.state[fieldName];

    if (!fieldState.blurred) {
      return null;
    }
    if (!fieldState.required && !fieldState.validators) {
      return null;
    }

    if (!fieldState.dirty) {
      return null;
    }

    return this.isFieldValid(fieldName) ? null : 'error';
  }

  getHelpText(fieldName) {
    const fieldState = this.state[fieldName];
    if (this.isFieldValid(fieldName)) {
      return '';
    }
    if (fieldState.required) {
      // Handle the required field
      if (Array.isArray(fieldState.value) && fieldState.value.length === 0) {
        return `${fieldState.displayName} is required`;
      } else if (fieldState.value === '') {
        return `${fieldState.displayName} is required`;
      }
    }
    return fieldState.helpBlock;
  }

  isFieldValid(fieldName) {
    const fieldState = this.state[fieldName];

    // Handle the required field
    if (Array.isArray(fieldState.value) && fieldState.value.length === 0) {
      return !fieldState.required;
    } else if (!fieldState.value) {
      return !fieldState.required;
    }

    if (!fieldState.validators) {
      return true;
    }
    return this.state[fieldName].validators.every(validator =>
      validator(this.state[fieldName].value),
    );
  }

  isFormDisabled() {
    return !Object.keys(this.state).every(fieldName =>
      this.isFieldValid(fieldName),
    );
  }

  handleChange(e, fieldName) {
    let value;
    if (this.state[fieldName].type === 'checkbox') {
      value = e.target.checked;
    } else {
      value = e.target.value;
    }

    this.setState(
      oldState => {
        const oldValue = oldState[fieldName];
        return {
          [fieldName]: {
            ...oldValue,
            value,
            dirty: true,
          },
        };
      },
      () => {
        Object.values(this.state).forEach(fieldState => {
          if (fieldState.fieldChangeHandler) {
            fieldState.fieldChangeHandler(fieldName, value);
          }
        });
      },
    );
  }

  async handleSubmit(e) {
    const fieldValues = Object.values(this.state).reduce(
      (accum, fieldState) => ({
        ...accum,
        [fieldState.name]: fieldState.value,
      }),
      {},
    );
    if (this.props.onSubmit) {
      try {
        await Promise.resolve(this.props.onSubmit.call(null, e, fieldValues));
      } catch (err) {
        console.error('horrible error', err);
      }
    }
  }

  handleBlur(e, fieldName) {
    this.setState(oldState => {
      const oldValue = oldState[fieldName];
      return {
        [fieldName]: {
          ...oldValue,
          blurred: true,
        },
      };
    });
  }

  generateInputGroup(fieldName) {
    const fieldState = this.state[fieldName];
    if (fieldState.type === 'text') {
      return (
        <Input
          invalid={this.getValidationState(fieldName) === 'error'}
          type="text"
          maxLength={fieldState.maxLength}
          value={this.state[fieldName].value}
          placeholder={`Enter a ${fieldState.displayName}`}
          onChange={e => this.handleChange(e, fieldName)}
          onBlur={e => this.handleBlur(e, fieldName)}
        />
      );
    } else if (fieldState.type === 'select') {
      return (
        <Input
          invalid={this.getValidationState(fieldName) === 'error'}
          type="select"
          value={this.state[fieldName].value}
          placeholder={`Select a ${fieldState.displayName}`}
          onChange={e => this.handleChange(e, fieldName)}
          onBlur={e => this.handleBlur(e, fieldName)}
        >
          {fieldState.options.map(optionName => (
            <option key={optionName} value={optionName}>
              {optionName}
            </option>
          ))}
        </Input>
      );
    } else if (fieldState.type === 'checkbox') {
      return (
        <FormGroup check>
          <Input
            invalid={this.getValidationState(fieldName) === 'error'}
            type="checkbox"
            value={this.state[fieldName].value}
            onChange={e => this.handleChange(e, fieldName)}
          />
        </FormGroup>
      );
    } else if (fieldState.type === 'array') {
      return (
        <ArrayInput
          handleChange={value => this.syncStateWithChild(value, fieldName)}
        />
      );
    } else if (fieldState.type === 'custom') {
      return (
        <fieldState.componentClass
          invalid={this.getValidationState(fieldName) === 'error'}
          handleChange={value => this.syncStateWithChild(value, fieldName)}
          handleBlur={e => this.handleBlur(e, fieldName)}
        />
      );
    }
    return null;
  }

  syncStateWithChild(value, fieldName) {
    this.handleChange({ target: { value } }, fieldName);
  }

  render() {
    const { className } = this.props;

    return (
      <Form
        className={`form-builder ${className}`}
        onSubmit={e => this.handleSubmit(e)}
      >
        {Object.keys(this.state).map(fieldName => {
          const fieldState = this.state[fieldName];
          if (fieldState.hidden) {
            return null;
          }
          return (
            // Figure out this for attribute setting
            <FormGroup row key={fieldName}>
              <Col sm={4}>
                <Label for={fieldName}>
                  {fieldState.displayName} {fieldState.required && '*'}
                </Label>
                {fieldState.tooltip && (
                  <React.Fragment>
                    <a href
                      className="form-builder-tooltip"
                      data-tip={fieldState.tooltip}
                      data-for={`tooltip-${fieldName}`}
                    >
                      <span> &#9432;</span>
                    </a>
                    <ReactTooltip
                      id={`tooltip-${fieldName}`}
                      place="right"
                      type="dark"
                      effect="float"
                    />
                  </React.Fragment>
                )}
              </Col>
              <Col sm={8}>
                {this.generateInputGroup(fieldName)}
                <FormFeedback>{this.getHelpText(fieldName)}</FormFeedback>
              </Col>
            </FormGroup>
          );
        })}
        <Row>
          <Col sm={{ sm: 6, offset: 6 }}>
            <NFButton
              block
              className="nf-button--orange nf-button--form"
              disabled={this.isFormDisabled()}
            >
              Submit
            </NFButton>
          </Col>
          <div className="nf-support-email">
            support@nffs.org | support@iconportal.com
          </div>
        </Row>
      </Form>
    );
  }
}

FormBuilder.defaultProps = {
  className: '',
};

FormBuilder.propTypes = {
  className: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
};



// WEBPACK FOOTER //
// ./src/components/FormBuilder/FormBuilder.jsx